import { isUndefined } from '@aloft/toolkit-shared';

import { RefObject, useEffect, useState } from 'react';

import { useGoogleMapsLoader } from './useGoogleMapsLoader.js';
import { useLatLngFromAddress } from './useLatLngFromAddress.js';

type Position = { lat: number; lng: number };

export const useStreetView = (
  address: string | undefined,
  fallbackPosition: Position | undefined,
  streetViewRef: RefObject<HTMLDivElement>,
) => {
  const [status, setStatus] = useState<'idle' | 'pending' | 'loaded' | 'error'>('idle');
  const [streetViewService, setStreetViewService] = useState<google.maps.StreetViewService>();
  const [streetViewPanorama, setStreetViewPanorama] = useState<google.maps.StreetViewPanorama>();

  const loader = useGoogleMapsLoader();

  const { latLng: geocodedPosition, status: geocodedStatus } = useLatLngFromAddress(address, true);

  // initialize street view, store in state
  useEffect(() => {
    const loadStreetView = async () => {
      if (streetViewRef.current) {
        setStatus('pending');

        loader.importLibrary('streetView').then((StreetViewLibrary) => {
          // we need StreetViewService for getPanorama below
          setStreetViewService(new StreetViewLibrary.StreetViewService());

          // initialize as hidden until we're sure we have a street view for the position
          setStreetViewPanorama(
            new StreetViewLibrary.StreetViewPanorama(streetViewRef.current as HTMLDivElement, {
              visible: false,
            }),
          );
        });
      }
    };

    // if street view service or panorama are not initialized, load them
    if (isUndefined(streetViewService) && isUndefined(streetViewPanorama)) {
      loadStreetView();
    }
  }, [loader, streetViewRef, streetViewPanorama, streetViewService]);

  // update position after init, or when position deps change in props
  useEffect(() => {
    const setStreetViewPosition = async (position: { lat: number; lng: number }) => {
      // return if street view service or panorama are not initialized
      if (isUndefined(streetViewService) || isUndefined(streetViewPanorama)) return;

      const hasStreetView = await streetViewService
        // attempt to create new, ephemeral panorama at position
        // getPanorama will search for any streetview within the specified radius
        // if the radius is too large, an irrelevant street view may be returned
        // if the radius is too small, large properties may not return a street view
        .getPanorama({ location: position, radius: 30 })
        // if error doesn't throw, street view exists
        .then(() => true)
        // if error is thrown, street view doesn't exist
        .catch(() => {
          return false;
        });

      if (!hasStreetView) {
        console.warn(`No street view found for ${position.lat}, ${position.lng}`);

        // hide street view so error message is visible
        streetViewPanorama.setOptions({ visible: false });
        setStatus('error');
        return;
      }

      // update position, show street view
      streetViewPanorama.setOptions({ position, visible: true });
      setStatus('loaded');
    };

    // we haven't gotten a geocoded position yet, return
    if (geocodedStatus === 'pending') return;

    // if we have a geocoded position, use it, otherwise use fallback
    const position = geocodedPosition ?? fallbackPosition;

    // we have no fallback and no geocoded position, error state
    if (isUndefined(position)) {
      setStatus('error');
      return;
    }

    setStreetViewPosition(position);
  }, [streetViewPanorama, fallbackPosition, geocodedPosition, geocodedStatus, streetViewService]);

  return status;
};
